/**
 * PLP Scroll Restore
 *
 * Saves the scroll position (card index + viewport %) when navigating from
 * the collection page to a product page, and restores it when the user
 * presses the browser back button.
 *
 * Flow:
 *   1. User clicks a product card on the PLP.
 *   2. This script intercepts the click (capture phase) and writes
 *      `?position=<index>#<vhPercent>` into the current history entry.
 *   3. The browser navigates to the PDP.
 *   4. User presses Back → browser restores the collection URL with the
 *      position params still present.
 *   5. This script detects `?position=` in the URL and polls via
 *      requestAnimationFrame until the product cards are rendered, then
 *      scrolls to the exact card at the same visual height as when it was
 *      clicked.
 */

(function () {
  'use strict';

  var PLP_SELECTOR = '.js-plp-searchspring';
  var CARD_SELECTOR = '.plp-card';
  var POSITION_PARAM = 'position';
  var SITE_HEADER_SELECTOR = 'site-header';

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  /** Scroll instantly, bypassing any CSS smooth-scroll behaviour. */
  function forceScroll(y) {
    var hs = document.documentElement.style;
    var bs = document.body.style;
    var prevH = hs.scrollBehavior;
    var prevB = bs.scrollBehavior;
    hs.scrollBehavior = bs.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    hs.scrollBehavior = prevH;
    bs.scrollBehavior = prevB;
  }

  /** Read `?position=<n>` from the current URL (returns NaN if absent). */
  function getPositionFromUrl() {
    return parseInt(new URLSearchParams(window.location.search).get(POSITION_PARAM));
  }

  /** Read `?page=<n>` from the current URL (returns 1 if absent). */
  function getPageFromUrl() {
    return parseInt(new URLSearchParams(window.location.search).get('page')) || 1;
  }

  /** Read `#<vhPercent>` from the current URL (returns NaN if absent). */
  function getVhFromHash() {
    return parseFloat(window.location.hash.replace('#', ''));
  }

  /**
   * When `?page=X` (X > 1) is present the store fetches pages 1…X before
   * rendering all cards.  Returns the minimum card count we must wait for
   * before the grid can be considered fully loaded for this page.
   *
   * Uses `window.collectionState.perPage` (set by plp-ss.liquid) when
   * available, then caps at `productsCount` so we never wait forever on a
   * small collection.
   */
  function getExpectedCardCount(page) {
    if (page <= 1) return 0;

    var cs = window.collectionState;
    var perPage = cs && cs.perPage;
    if (!perPage) return 0;

    var expected = page * perPage;

    // Cap at total products in the collection so we don't wait indefinitely
    // when the last page is partially filled or filters reduce the result set
    var total = cs && cs.productsCount;
    return total ? Math.min(expected, total) : expected;
  }

  // --------------------------------------------------------------------------
  // Scroll restoration
  // --------------------------------------------------------------------------

  /**
   * Poll via rAF until the target product card is in the DOM, then scroll to
   * it so it appears at the same visual position as when the user left.
   *
   * When `?page=X` (X > 1) is present we wait until the full multi-page grid
   * has rendered before scrolling, so the card offsets are stable.
   */
  function scrollToSavedPosition() {
    var position = getPositionFromUrl();
    if (isNaN(position) || position < 0) return;

    var page = getPageFromUrl();
    var expectedTotal = getExpectedCardCount(page);

    // Allow more time for multi-page loads. Each iteration in the store loop
    // does a sequential SearchSpring + Shopify GraphQL round-trip (~0.5–2 s
    // per page), so we scale the budget by `page` to avoid timing out before
    // the grid is fully rendered. 60fps ⇒ 250 frames ≈ 4.1 s per page.
    var MAX_ATTEMPTS = page > 1 ? Math.max(300, page * 250) : 120;

    var vhPercent = getVhFromHash();
    var hasVhTarget = !isNaN(vhPercent);
    var attempts = 0;
    var stableFrames = 0;   // consecutive frames where card count didn't grow
    var lastCardCount = 0;
    var targetPos = 0;

    function executeScroll() {
      attempts++;

      var cards = document.querySelectorAll(PLP_SELECTOR + ' ' + CARD_SELECTOR);

      // No cards yet — keep waiting (covers the time the store fetches data)
      if (!cards.length) {
        lastCardCount = 0;
        stableFrames = 0;
        if (attempts < MAX_ATTEMPTS) requestAnimationFrame(executeScroll);
        return;
      }

      // ── Multi-page wait logic ────────────────────────────────────────────
      if (page > 1) {
        if (expectedTotal > 0) {
          // We know how many cards to expect — wait until we have them all
          if (cards.length < expectedTotal) {
            lastCardCount = cards.length;
            stableFrames = 0;
            if (attempts < MAX_ATTEMPTS) requestAnimationFrame(executeScroll);
            return;
          }
        } else {
          // perPage unknown — wait until the card count stops growing for
          // 6 consecutive frames (~100 ms) so the grid has settled
          if (cards.length !== lastCardCount) {
            lastCardCount = cards.length;
            stableFrames = 0;
            if (attempts < MAX_ATTEMPTS) requestAnimationFrame(executeScroll);
            return;
          }
          stableFrames++;
          if (stableFrames < 6) {
            if (attempts < MAX_ATTEMPTS) requestAnimationFrame(executeScroll);
            return;
          }
        }
      }
      // ── End multi-page wait ──────────────────────────────────────────────

      // Target card still not in DOM (position beyond what loaded, e.g. when
      // the collection has fewer products than page * perPage after filtering)
      if (!cards[position]) {
        if (attempts < MAX_ATTEMPTS) requestAnimationFrame(executeScroll);
        return;
      }

      var el = cards[position];

      if (hasVhTarget) {
        // Restore card to the exact same % of the viewport height as when clicked
        var targetViewportY = (vhPercent / 100) * window.innerHeight;
        targetPos = el.getBoundingClientRect().top + window.scrollY - targetViewportY;
      } else {
        // Fallback: place card just below the sticky header
        var headerEl = document.querySelector(SITE_HEADER_SELECTOR);
        targetPos = el.getBoundingClientRect().top + window.scrollY - (headerEl ? headerEl.offsetHeight : 0);
      }

      forceScroll(targetPos);

      // Keep retrying until we land within 2 px (layout can shift during render)
      if (Math.abs(window.scrollY - targetPos) < 2 && attempts > 5) {
        // Clean up the hash from the URL once scrolling is done
        if (hasVhTarget) {
          try {
            history.replaceState(
              history.state,
              '',
              window.location.pathname + window.location.search
            );
          } catch (e) {}
        }
      } else if (attempts < MAX_ATTEMPTS) {
        requestAnimationFrame(executeScroll);
      }
    }

    requestAnimationFrame(executeScroll);
  }

  // --------------------------------------------------------------------------
  // Save navigation state before leaving the PLP
  // --------------------------------------------------------------------------

  /**
   * Called just before the browser navigates away from the PLP.
   * Writes `?position=<index>#<vhPercent>` into the current history entry so
   * it survives the round-trip to the PDP and back.
   */
  function saveNavigationState(cardEl) {
    var plp = document.querySelector(PLP_SELECTOR);
    if (!plp) return;

    var allCards = plp.querySelectorAll(CARD_SELECTOR);
    var position = Array.from(allCards).indexOf(cardEl);
    if (position < 0) return;

    var url = new URL(window.location.href);
    url.searchParams.set(POSITION_PARAM, position);

    // Store the card's position as a % of viewport height so we can restore
    // the exact visual context when the user comes back
    var vhPercent = (cardEl.getBoundingClientRect().top / window.innerHeight * 100).toFixed(2);
    url.hash = vhPercent;

    try {
      history.replaceState(history.state || {}, '', url.href);
    } catch (e) {}
  }

  // --------------------------------------------------------------------------
  // Click interception (capture phase — fires before the card's own handler)
  // --------------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    // Only act when we are on a PLP
    var plp = document.querySelector(PLP_SELECTOR);
    if (!plp) return;

    // Make sure the click is on a link inside a product card
    var link = e.target.closest('a[href]');
    if (!link) return;

    var card = e.target.closest(CARD_SELECTOR);
    if (!card || !plp.contains(card)) return;

    saveNavigationState(card);
  }, true /* capture */);

  // --------------------------------------------------------------------------
  // Initialisation
  // --------------------------------------------------------------------------

  function tryRestoreScroll() {
    if (isNaN(getPositionFromUrl()) || getPositionFromUrl() < 0) return;
    scrollToSavedPosition();
  }

  // Prevent the browser from automatically restoring scroll on history
  // navigation (the store also does this, but we want it set as early as
  // possible to avoid a flash of the wrong position)
  if (typeof history !== 'undefined') {
    history.scrollRestoration = 'manual';
  }

  // Start polling as soon as the DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryRestoreScroll);
  } else {
    tryRestoreScroll();
  }

  // Handle browser back/forward navigation
  window.addEventListener('popstate', function () {
    // Give the store a tick to handle its own popstate listener first
    setTimeout(tryRestoreScroll, 50);
  });
})();
